import { useCallback, useRef, useEffect } from "react";

type SoundType = "notification" | "call";

/**
 * Custom hook for playing notification sounds.
 * Manages audio playback with autoplay policies and browser compatibility.
 */
export const useNotificationSound = () => {
  const notificationAudioRef = useRef<HTMLAudioElement | null>(null);
  const callAudioRef = useRef<HTMLAudioElement | null>(null);
  const isInitializedRef = useRef(false);

  // Initialize audio elements once
  useEffect(() => {
    if (!isInitializedRef.current) {
      notificationAudioRef.current = new Audio("/assets/sounds/notification-tone.mp3");
      notificationAudioRef.current.preload = "auto";

      callAudioRef.current = new Audio("/assets/sounds/ring.mp3");
      callAudioRef.current.preload = "auto";

      isInitializedRef.current = true;
    }

    return () => {
      if (notificationAudioRef.current) {
        notificationAudioRef.current.pause();
        notificationAudioRef.current.src = "";
        notificationAudioRef.current = null;
      }
      if (callAudioRef.current) {
        callAudioRef.current.pause();
        callAudioRef.current.src = "";
        callAudioRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, []);

  /**
   * Play notification sound
   * @param type - Type of sound to play ("notification" or "call")
   * @param loop - Whether to loop the sound
   * @param volume - Volume level (0.0 to 1.0)
   * @returns Promise that resolves when sound starts playing
   */
  const play = useCallback(async (type: SoundType = "notification", loop = false, volume = 0.5) => {
    try {
      const audioRef = type === "call" ? callAudioRef.current : notificationAudioRef.current;

      if (!audioRef) {
        console.warn(`[NotificationSound] Audio not initialized for ${type}`);
        return;
      }

      // Reset audio to start
      audioRef.currentTime = 0;
      audioRef.loop = loop;
      audioRef.volume = Math.max(0, Math.min(1, volume));

      // Try to play - browsers may block autoplay
      await audioRef.play();
      console.log(`[NotificationSound] Playing ${type} sound`);
    } catch (error) {
      // Autoplay was prevented - this is expected in many browsers
      console.warn(`[NotificationSound] Autoplay prevented for ${type}:`, error);
      // You could show a UI prompt here to enable sounds
    }
  }, []);

  /**
   * Stop playing notification sound
   * @param type - Type of sound to stop ("notification" or "call"), if undefined stops all
   */
  const stop = useCallback((type?: SoundType) => {
    if (type === "call" || type === undefined) {
      if (callAudioRef.current) {
        callAudioRef.current.pause();
        callAudioRef.current.currentTime = 0;
        console.log("[NotificationSound] Stopped call sound");
      }
    }

    if (type === "notification" || type === undefined) {
      if (notificationAudioRef.current) {
        notificationAudioRef.current.pause();
        notificationAudioRef.current.currentTime = 0;
        console.log("[NotificationSound] Stopped notification sound");
      }
    }
  }, []);

  /**
   * Check if sound is currently playing
   * @param type - Type of sound to check ("notification" or "call")
   */
  const isPlaying = useCallback((type: SoundType) => {
    const audioRef = type === "call" ? callAudioRef.current : notificationAudioRef.current;
    return audioRef ? !audioRef.paused : false;
  }, []);

  return { play, stop, isPlaying };
};
